import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Cart timer. One controller per page:
 * - Starts the countdown when the cart goes from empty to having items; the start time is kept in
 *   localStorage, so closing/reopening the drawer, navigating and reloading keep the same deadline.
 * - Updates every `[data-wearth-cart-timer]` display (the drawer re-renders, so displays come and go).
 * - At zero, empties the cart with the Cart API (`/cart/clear.js`) and fires the theme's cart
 *   update event so the drawer and cart count re-render into the empty state.
 * - Any cart change re-reads the real item count from `/cart.js` (theme events only report the
 *   quantity just added), so removing the last item resets the timer.
 */
const STORAGE_KEY = 'wearth-cart-timer';
const SOURCE = 'wearth-cart-timer';
const URGENT_SECONDS = 60;

const config = (() => {
  try {
    return JSON.parse(document.getElementById('WearthCartTimerConfig')?.textContent || '{}');
  } catch {
    return {};
  }
})();
const duration = Number(config.seconds || 300) * 1000;
let clearing = false;
/** @type {number | undefined} */
let syncTimer;

/** @returns {number | null} */
function readStart() {
  try {
    const value = Number(window.localStorage.getItem(STORAGE_KEY));
    return value > 0 ? value : null;
  } catch {
    return null;
  }
}

/** @param {number | null} value */
function writeStart(value) {
  try {
    if (value) window.localStorage.setItem(STORAGE_KEY, String(value));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked: the timer then lasts for this page view only
  }
  memoryStart = value;
}

let memoryStart = readStart();

/** Starts the timer for a cart that has items, stops it for an empty one */
function syncItemCount(/** @type {number} */ count) {
  const start = readStart() ?? memoryStart;
  if (count > 0 && !start) writeStart(Date.now());
  if (count === 0 && start) writeStart(null);
  render();
}

/** @returns {Promise<any>} the cart, or null if it couldn't be read */
async function refreshFromCart() {
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    syncItemCount(Number(cart.item_count) || 0);
    return cart;
  } catch {
    // Network hiccup: keep the current state
    return null;
  }
}

async function clearCart() {
  if (clearing || config.designMode) return;
  clearing = true;
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/clear.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart clear failed: ${response.status}`);
    const cart = await response.json();
    writeStart(null);
    // Same event the theme uses after cart changes: the drawer, cart page and cart count re-render
    document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { source: SOURCE, itemCount: 0 }));
  } catch (error) {
    console.error(error);
    // Try again shortly; the deadline stays in place
    window.setTimeout(() => (clearing = false), 5000);
    return;
  }
  clearing = false;
}

function render() {
  const start = readStart() ?? memoryStart;
  const remaining = start ? Math.max(0, Math.ceil((start + duration - Date.now()) / 1000)) : Math.ceil(duration / 1000);
  const text = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(remaining % 60).padStart(2, '0')}`;

  for (const display of document.querySelectorAll('[data-wearth-cart-timer]')) {
    const time = display.querySelector('[data-time]');
    if (time && time.textContent !== text) time.textContent = text;
    display.toggleAttribute('data-urgent', Boolean(start) && remaining <= URGENT_SECONDS);
  }

  if (start && remaining === 0) clearCart();
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = /** @type {CustomEvent} */ (event).detail;
  if (detail?.data?.source === SOURCE) return;
  // Wait for the theme's own cart request to settle, then read the real count
  window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(refreshFromCart, 400);
});

// Another tab started, reset or cleared the timer: if the cart is now empty, re-render this tab too
window.addEventListener('storage', async (event) => {
  if (event.key !== STORAGE_KEY) return;
  memoryStart = readStart();
  render();
  if (memoryStart) return;
  const cart = await refreshFromCart();
  if (cart && Number(cart.item_count) === 0) {
    document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { source: SOURCE, itemCount: 0 }));
  }
});

syncItemCount(Number(config.itemCount) || 0);
window.setInterval(render, 500);
